import type { Address, AddressValue } from "./types"

// Keep Stripe.js on the same release train as the server-side Stripe API.
export const STRIPE_JS_URL = "https://js.stripe.com/dahlia/stripe.js"

export type StripeCheckoutSession = {
    canConfirm?: boolean
    billingAddress?: { name?: string | null; address?: Partial<Address> } | null
}

export type StripeActionResult = { type?: "success" | "error"; error?: { message?: string; type?: string; code?: string } }

export type StripeExpressCheckoutConfirmEvent = {
    paymentFailed(payload?: { reason?: "fail"; message?: string }): void
}

export type StripeActions = {
    updateEmail(value: string): Promise<StripeActionResult>
    updatePhoneNumber(value: string): Promise<StripeActionResult>
    updateShippingAddress(value: AddressValue): Promise<StripeActionResult>
    updateBillingAddress(value: { name?: string; address: Address }): Promise<StripeActionResult>
    getSession(): StripeCheckoutSession
    validateElements(): Promise<StripeActionResult>
    confirm(options?: { expressCheckoutConfirmEvent?: StripeExpressCheckoutConfirmEvent }): Promise<StripeActionResult>
}

export type StripeElement = {
    mount(target: HTMLElement | string): void
    unmount(): void
    destroy?(): void
    on(event: string, callback: (event: any) => void): void
}

export type StripeExpressCheckoutElement = Omit<StripeElement, "on"> & {
    on(event: "availablepaymentmethodschange", callback: (event: { paymentMethods?: Record<string, { available: boolean } | undefined> }) => void): void
    on(event: "click", callback: (event: { expressPaymentType: string; resolve(): void }) => void): void
    on(event: "confirm", callback: (event: StripeExpressCheckoutConfirmEvent) => void): void
}

export type StripeCheckout = {
    loadActions(): Promise<{ type: "success"; actions: StripeActions } | { type: "error"; error: { message?: string } }>
    createBillingAddressElement(options?: Record<string, unknown>): StripeElement
    createExpressCheckoutElement(options?: Record<string, unknown>): StripeExpressCheckoutElement
    createPaymentElement(options?: Record<string, unknown>): StripeElement
    on(event: "change", callback: (session: StripeCheckoutSession) => void): void
}

export type StripeClient = {
    initCheckoutElementsSdk(options: { clientSecret: string; elementsOptions?: Record<string, unknown>; defaultValues?: Record<string, unknown> }): StripeCheckout
}

export function initialiseStripeCheckout(
    stripe: StripeClient,
    options: { clientSecret: string; elementsOptions?: Record<string, unknown>; defaultValues?: Record<string, unknown> },
): StripeCheckout {
    if (typeof stripe.initCheckoutElementsSdk !== "function") throw new Error("Stripe.js Checkout Elements SDK is unavailable.")
    return stripe.initCheckoutElementsSdk(options)
}

export function loadStripeJs() {
    if (typeof window === "undefined" || typeof document === "undefined") return Promise.reject(new Error("Stripe is only available in the browser."))
    if (window.Stripe) return Promise.resolve()
    if (window.__arcticHireStripePromise) return window.__arcticHireStripePromise
    window.__arcticHireStripePromise = new Promise<void>((resolve, reject) => {
        const existing = document.querySelector<HTMLScriptElement>(`script[src="${STRIPE_JS_URL}"]`)
        const script = existing ?? document.createElement("script")
        const fail = (message: string) => {
            // A failed early download must not poison a later payment attempt.
            script.remove()
            window.__arcticHireStripePromise = undefined
            reject(new Error(message))
        }
        const onLoad = () => window.Stripe ? resolve() : fail("Stripe.js did not initialise.")
        script.addEventListener("load", onLoad, { once: true })
        script.addEventListener("error", () => fail("Stripe.js could not be loaded."), { once: true })
        if (!existing) {
            script.src = STRIPE_JS_URL
            script.async = true
            document.head.appendChild(script)
        }
    })
    return window.__arcticHireStripePromise
}

export function resultError(result: StripeActionResult) {
    return result.type === "error" || result.error ? result.error?.message || "Stripe could not save those details." : ""
}

declare global {
    interface Window {
        Stripe?: (publishableKey: string) => StripeClient
        __arcticHireStripePromise?: Promise<void>
        dataLayer?: Array<Record<string, unknown>>
        gtag?: (...args: unknown[]) => void
    }
}
